import {uploadFile} from "@/api/client/communications";
import {inquiryForm} from "@/api/client/forms";
import {DynamicForm} from "@/components/client/dynamic-form";
import {Form} from "@/components/ui/form";
import {
    collectFilesForUpload,
    filterActiveFormQuestions,
    generateDefaultValues,
    generateZodSchema,
    processDefaultValues,
} from "@/lib/utils";
import {useStore} from "@/store";
import {zodResolver} from "@hookform/resolvers/zod";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useForm} from "react-hook-form";
import {useTranslation} from "react-i18next";
import {useNavigate} from "react-router-dom";
import {toast} from "sonner";

const getAdminQuestions = (t) => [
    {
        id: "date",
        alias: "date",
        type: "date",
        label: t("headColumns.communications.date"),
        required: true,
        screen: 1,
        position: -2,
        active: true,
        visibility: { form: true, summary: true },
    },
    {
        id: "source",
        alias: "source",
        type: "simpleSelect",
        label: t("ClientLayout.forms.source.label"),
        options: [
            { value: "phone", text: t("ClientLayout.forms.source.options.phone") },
            { value: "interview", text: t("ClientLayout.forms.source.options.interview") },
            { value: "email", text: t("ClientLayout.forms.source.options.email") },
            { value: "letter", text: t("ClientLayout.forms.source.options.letter") },
        ],
        required: true,
        screen: 1,
        position: -1,
        active: true,
        visibility: { form: true, summary: true },
    },
];

const withAdminDefaults = (context, values) =>
    context === "admin" ? { date: "", source: "", ...(values || {}) } : values;

const normalizeCommunicationDate = (date) =>
    typeof date === "string" && date.trim() !== "" ? `${date}T00:00:00` : undefined;

export function InquiryForm({values, context = "public"}) {
    const {i18n} = useTranslation();
    const [questions, setQuestions] = useState([]);
    const [defaultValues, setDefaultValues] = useState(null);
    const createCommunication = useStore((state) => state.createCommunication);

    const navigate = useNavigate();
    const {t} = useTranslation();
    const formQuestions = context === "admin" ? [...getAdminQuestions(t), ...questions] : questions;
    const formSchema = useMemo(() => generateZodSchema(
        context === "admin" ? [...getAdminQuestions(t), ...questions] : questions,
        t,
    ), [context, questions, t]);

    const form = useForm({
        defaultValues: defaultValues || {},
    });

    useEffect(() => {
        // Refresh translated validation without resetting any entered answers.
        form.control._resolver = zodResolver(formSchema);
    }, [formSchema, form]);

    const onSubmit = useCallback(
        async (values, _token) => {
            try {
                const filesToUpload = collectFilesForUpload(values, questions);
                const {confirmPassword, files, date, ...filteredValues} = values;
                const payloadValues = {...filteredValues};
                questions
                    .filter((question) => question?.type === "file")
                    .forEach((question) => {
                        delete payloadValues[question.alias];
                    });
                const communicationDate = normalizeCommunicationDate(date);
                if (communicationDate) {
                    payloadValues.date = communicationDate;
                }
                const language = i18n.language;

                // Crear la comunicación
                const communication = await createCommunication({
                    type: "consultation",
                    risk: "medium",
                    language: language,
                    ...payloadValues,
                });

                // Subir los archivos usando el ID de la comunicación
                if (filesToUpload.length > 0) {
                    const uploadPromises = filesToUpload.map(({file, questionId}) =>
                        uploadFile(file, communication.id, "public", "communication", "", questionId, true)
                    );
                    await Promise.all(uploadPromises);
                }

                form.reset(defaultValues);
                if (context === "admin") {
                    navigate(`/admin/queries`);
                } else {
                    navigate(`/summary/${communication.accessToken}`);
                }

            } catch (error) {
                toast.error(t("ClientLayout.forms.inquiry.error"));
            }
        },
        [t, form, createCommunication, questions, i18n.language, context, navigate, defaultValues],
    );

    useEffect(() => {
        let active = true;
        const fetchData = async () => {
            const formData = await inquiryForm();
            if (!active) return;
            const questions = formData.questions;

            const filteredQuestions = filterActiveFormQuestions(questions);

            const processedValues = withAdminDefaults(context, processDefaultValues(values));
            const defaultValues =
                processedValues || withAdminDefaults(context, generateDefaultValues(filteredQuestions));
            form.reset(defaultValues);
            setDefaultValues(defaultValues);
            setQuestions(filteredQuestions);
        };
        fetchData();
        return () => { active = false; };
    }, [context, form, values]);

    return (
        questions.length > 0 && (
            <div className="border border-dashed rounded-lg p-6 border-primary animate-fade-in">
                <Form {...form}>
                    <DynamicForm
                        questions={formQuestions}
                        allQuestions={formQuestions}
                        form={form}
                        onSubmit={onSubmit}
                        disabled={!!values}
                        view={values ? "summary" : "form"}
                        context={context}
                    />
                </Form>
            </div>
        )
    );
}
